import api from './api';

// ============================================
// TYPES
// ============================================

export type OrderStatus = 'pendente' | 'confirmado' | 'preparando' | 'enviado' | 'entregue' | 'cancelado';

export interface OrderItem {
  id: number;
  productId?: number;
  productName: string;
  productDescription?: string;
  productImage?: string;
  unitPrice: number;
  quantity: number;
  subtotal: number;
  notes?: string;
}

export interface OrderHistory {
  id: number;
  previousStatus?: string;
  newStatus: string;
  description?: string;
  userName?: string;
  createdAt: string;
}

export interface OrderRefund {
  id: number;
  amount: number;
  reason: string;
  status: 'pendente' | 'aprovado' | 'processado' | 'rejeitado';
  paymentReference?: string;
  userName?: string;
  createdAt: string;
  processedAt?: string;
}

export interface Order {
  id: number;
  numero: string;
  storeId: number;
  storeName?: string;
  storeLogo?: string;
  clientId?: number;
  clientName?: string;
  clientEmail?: string;
  clientPhone?: string;
  deliveryAddress?: string;
  subtotal: number;
  deliveryFee: number;
  discount: number;
  total: number;
  paymentMethod?: string;
  status: OrderStatus;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  confirmedAt?: string;
  preparingAt?: string;
  shippedAt?: string;
  deliveredAt?: string;
  cancelledAt?: string;
  cancellationReason?: string;
  items?: OrderItem[];
  history?: OrderHistory[];
  refunds?: OrderRefund[];
}

export interface OrderStats {
  total: number;
  pending: number;
  confirmed: number;
  preparing: number;
  shipped: number;
  delivered: number;
  cancelled: number;
  totalValue: number;
  averageValue: number;
  todayOrders: number;
  todayValue: number;
  periodStats?: {
    date: string;
    orders: number;
    value: number;
  }[];
}

export interface StoreSummary {
  storeId: number;
  storeName: string;
  totalOrders: number;
  pendingOrders: number;
  deliveredOrders: number;
  totalValue: number;
  averageOrderValue: number;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
  needsSetup?: boolean;
}

export interface OrderFilters {
  page?: number;
  limit?: number;
  status?: OrderStatus;
  storeId?: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: 'date' | 'total' | 'status';
  sortOrder?: 'asc' | 'desc';
}

// ============================================
// SERVICE
// ============================================

class OrdersService {
  // ============================================
  // SETUP
  // ============================================

  async setupTables(): Promise<{
    success: boolean;
    message: string;
    created: string[];
    updated: string[];
  }> {
    try {
      const response = await api.post('/admin/shop/orders/setup');
      return response.data;
    } catch (error) {
      console.error('Setup orders tables error:', error);
      throw error;
    }
  }

  // ============================================
  // ORDERS
  // ============================================

  async getOrders(params?: OrderFilters): Promise<PaginatedResponse<Order>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.limit) queryParams.append('limit', String(params.limit));
      if (params?.status) queryParams.append('status', params.status);
      if (params?.storeId) queryParams.append('storeId', String(params.storeId));
      if (params?.search) queryParams.append('search', params.search);
      if (params?.dateFrom) queryParams.append('dateFrom', params.dateFrom);
      if (params?.dateTo) queryParams.append('dateTo', params.dateTo);
      if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

      const response = await api.get(`/admin/shop/orders?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get orders error:', error);
      throw error;
    }
  }

  async getOrderStats(storeId?: number, period?: number): Promise<{ success: boolean; data: OrderStats; needsSetup?: boolean }> {
    try {
      const queryParams = new URLSearchParams();
      if (storeId) queryParams.append('storeId', String(storeId));
      if (period) queryParams.append('period', String(period));

      const response = await api.get(`/admin/shop/orders/stats?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get order stats error:', error);
      throw error;
    }
  }

  async getOrder(id: number): Promise<{ success: boolean; data: Order; needsSetup?: boolean }> {
    try {
      const response = await api.get(`/admin/shop/orders/${id}`);
      return response.data;
    } catch (error) {
      console.error('Get order error:', error);
      throw error;
    }
  }

  async updateOrderStatus(
    id: number,
    status: OrderStatus,
    description?: string
  ): Promise<{ success: boolean; message: string; data: { previousStatus: string; newStatus: string } }> {
    try {
      const response = await api.put(`/admin/shop/orders/${id}/status`, {
        status,
        description,
      });
      return response.data;
    } catch (error) {
      console.error('Update order status error:', error);
      throw error;
    }
  }

  async getOrderHistory(id: number): Promise<{ success: boolean; data: OrderHistory[]; needsSetup?: boolean }> {
    try {
      const response = await api.get(`/admin/shop/orders/${id}/history`);
      return response.data;
    } catch (error) {
      console.error('Get order history error:', error);
      throw error;
    }
  }

  // ============================================
  // REFUNDS
  // ============================================

  async createRefund(
    orderId: number,
    amount: number,
    reason: string
  ): Promise<{ success: boolean; message: string; data: { id: number; orderId: number; amount: number; reason: string; status: string } }> {
    try {
      const response = await api.post(`/admin/shop/orders/${orderId}/refund`, {
        amount,
        reason,
      });
      return response.data;
    } catch (error) {
      console.error('Create refund error:', error);
      throw error;
    }
  }

  async updateRefund(
    refundId: number,
    status: 'pendente' | 'aprovado' | 'processado' | 'rejeitado',
    paymentReference?: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.put(`/admin/shop/orders/refunds/${refundId}`, {
        status,
        paymentReference,
      });
      return response.data;
    } catch (error) {
      console.error('Update refund error:', error);
      throw error;
    }
  }

  // ============================================
  // STORE SUMMARY
  // ============================================

  async getStoresSummary(): Promise<{ success: boolean; data: StoreSummary[]; needsSetup?: boolean }> {
    try {
      const response = await api.get('/admin/shop/orders/stores/summary');
      return response.data;
    } catch (error) {
      console.error('Get stores summary error:', error);
      throw error;
    }
  }

  // ============================================
  // DASHBOARD - Analytics avançados
  // ============================================

  async getStoreDashboard(storeId: number | string, period: number = 30): Promise<{
    success: boolean;
    data: {
      kpis: {
        totalOrders: number;
        totalRevenue: number;
        averageOrderValue: number;
        pendingOrders: number;
        deliveredOrders: number;
      };
      salesByDay: { date: string; orders: number; revenue: number }[];
      ordersByStatus: { status: string; count: number; revenue: number }[];
      recentOrders: any[];
    };
  }> {
    try {
      const response = await api.get(`/admin/shop/orders/dashboard/store/${storeId}?period=${period}`);
      return response.data;
    } catch (error) {
      console.error('Get store dashboard error:', error);
      throw error;
    }
  }

  async getSalesChart(params?: { storeId?: number | string; period?: number; groupBy?: 'day' | 'week' | 'month' }): Promise<{
    success: boolean;
    data: { period: string; orders: number; revenue: number }[];
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.storeId) queryParams.append('storeId', String(params.storeId));
      if (params?.period) queryParams.append('period', String(params.period));
      if (params?.groupBy) queryParams.append('groupBy', params.groupBy);

      const response = await api.get(`/admin/shop/orders/dashboard/sales-chart?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get sales chart error:', error);
      throw error;
    }
  }

  async getTopProducts(params?: { storeId?: number | string; period?: number; limit?: number }): Promise<{
    success: boolean;
    data: {
      productId?: number;
      productName: string;
      totalQuantity: number;
      totalRevenue: number;
      orderCount: number;
    }[];
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.storeId) queryParams.append('storeId', String(params.storeId));
      if (params?.period) queryParams.append('period', String(params.period));
      if (params?.limit) queryParams.append('limit', String(params.limit));

      const response = await api.get(`/admin/shop/orders/dashboard/top-products?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get top products error:', error);
      throw error;
    }
  }

  async getStoreRanking(params?: { period?: number; limit?: number }): Promise<{
    success: boolean;
    data: {
      rank: number;
      storeId: number;
      storeName: string;
      totalOrders: number;
      totalRevenue: number;
      avgOrderValue: number;
    }[];
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.period) queryParams.append('period', String(params.period));
      if (params?.limit) queryParams.append('limit', String(params.limit));

      const response = await api.get(`/admin/shop/orders/dashboard/store-ranking?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get store ranking error:', error);
      throw error;
    }
  }

  async getHourlyDistribution(params?: { storeId?: number | string; period?: number }): Promise<{
    success: boolean;
    data: { hour: number; orders: number }[];
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.storeId) queryParams.append('storeId', String(params.storeId));
      if (params?.period) queryParams.append('period', String(params.period));

      const response = await api.get(`/admin/shop/orders/dashboard/hourly-distribution?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get hourly distribution error:', error);
      throw error;
    }
  }

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  getStatusLabel(status: OrderStatus): string {
    const labels: Record<OrderStatus, string> = {
      pendente: 'Pendente',
      confirmado: 'Confirmado',
      preparando: 'Em Preparação',
      enviado: 'Enviado',
      entregue: 'Entregue',
      cancelado: 'Cancelado',
    };
    return labels[status] || status;
  }

  getStatusColor(status: OrderStatus): 'warning' | 'info' | 'primary' | 'secondary' | 'success' | 'error' {
    const colors: Record<OrderStatus, 'warning' | 'info' | 'primary' | 'secondary' | 'success' | 'error'> = {
      pendente: 'warning',
      confirmado: 'info',
      preparando: 'primary',
      enviado: 'secondary',
      entregue: 'success',
      cancelado: 'error',
    };
    return colors[status] || 'info';
  }

  getNextStatuses(currentStatus: OrderStatus): OrderStatus[] {
    const transitions: Record<OrderStatus, OrderStatus[]> = {
      pendente: ['confirmado', 'cancelado'],
      confirmado: ['preparando', 'cancelado'],
      preparando: ['enviado', 'cancelado'],
      enviado: ['entregue', 'cancelado'],
      entregue: [],
      cancelado: [],
    };
    return transitions[currentStatus] || [];
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-CV', {
      style: 'currency',
      currency: 'CVE',
    }).format(value);
  }
}

export default new OrdersService();
